/**
 * Server-side safe URL and school context utilities
 * No "use client" directive - can be imported by middleware and server components
 */

import { PLATFORM_NAME } from "./constants";

/**
 * Extract school slug from subdomain only
 */
const localLog = (...args: unknown[]) => {
  return;
  // console.log("🖥️ [url-utils]", ...args);
};

export function extractSchoolSlug(host: string): string | null {
  localLog("Extracting school slug from host:", { host });

  // Extract subdomain from host (e.g., "school-name.example.com" -> "school-name")
  const parts = host.split(".");
  if (parts.length >= 3) {
    // Assuming format: subdomain.domain.tld
    const subdomain = parts[0];
    // Filter out common subdomains that are not school slugs
    if (!["www", "api", "admin"].includes(subdomain)) {
      localLog("School slug extracted:", {
        host,
        subdomain,
        parts,
      });
      return subdomain;
    } else {
      localLog("Subdomain ignored (reserved):", {
        host,
        subdomain,
        parts,
      });
    }
  } else {
    localLog("No subdomain found:", { host, parts });
  }
  return null;
}

/**
 * Legacy function that works with both host and path (for backward compatibility)
 */
export function extractSlug(host: string, path: string): string | null {
  localLog("extractSlug called (legacy):", { host, path });
  // For now, just use the subdomain-only approach
  return extractSchoolSlug(host);
}

/**
 * Build school-specific URLs
 */
export function buildSchoolUrl(
  slug: string,
  locale: string = "en",
  path: string = "",
  forceFullUrl: boolean = false
): string {
  // Clean up the path to avoid double slashes
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  localLog("Building school URL:", {
    slug,
    locale,
    originalPath: path,
    cleanPath,
    forceFullUrl,
    isClientSide: typeof window !== "undefined",
  });

  // For client-side
  if (typeof window !== "undefined") {
    const currentHost = window.location.host;
    const protocol = window.location.protocol;

    // Check if we're in development (localhost)
    const isLocalhost =
      currentHost.includes("localhost") ||
      currentHost.includes("127.0.0.1") ||
      currentHost.includes(":5002");

    localLog("Environment detection:", {
      currentHost,
      isLocalhost,
      protocol,
    });

    // For development/localhost, still use subdomain structure but with localhost
    if (isLocalhost) {
      // Extract the port from current host if present
      const portMatch = currentHost.match(/:(\d+)$/);
      const port = portMatch ? portMatch[1] : "5002";

      // Build subdomain URL for localhost
      const resultUrl = `${protocol}//${slug}.${PLATFORM_NAME}.com:${port}/${locale}${cleanPath}`;
      // const resultUrl = `${protocol}//${slug}.localhost:${port}/${locale}${cleanPath}`;
      localLog("Development mode: using subdomain with localhost:", {
        resultUrl,
        slug,
        port,
        reason: "localhost detected but maintaining subdomain structure",
      });
      return resultUrl;
    }

    // Production: Extract base domain (remove any existing subdomain)
    let baseDomain: string;
    const hostParts = currentHost.split(".");

    if (hostParts.length >= 3) {
      // Has subdomain: "school.${PLATFORM_NAME}.com" -> "${PLATFORM_NAME}.com"
      baseDomain = hostParts.slice(1).join(".");
    } else {
      // No subdomain: "${PLATFORM_NAME}.com" -> "${PLATFORM_NAME}.com"
      baseDomain = currentHost;
    }

    // If we're already on the correct subdomain and not forcing full URL, use relative path
    if (!forceFullUrl && currentHost.startsWith(`${slug}.`)) {
      const resultUrl = `/${locale}${cleanPath}`;
      localLog("Already on correct subdomain, using relative path:", {
        currentHost,
        slug,
        resultUrl,
      });
      return resultUrl;
    }

    // Build full subdomain URL (either forced or different subdomain)
    const resultUrl = `${protocol}//${slug}.${baseDomain}/${locale}${cleanPath}`;
    localLog("Building full subdomain URL:", {
      currentHost,
      protocol,
      baseDomain,
      slug,
      locale,
      cleanPath,
      resultUrl,
      forced: forceFullUrl,
    });
    return resultUrl;
  }

  // Fallback for server-side rendering - use regular paths
  const fallbackUrl = `/${locale}${cleanPath}`;
  localLog("Server-side fallback URL:", {
    slug,
    locale,
    cleanPath,
    fallbackUrl,
  });
  return fallbackUrl;
}

/**
 * Check if current request is for a school context (subdomain only)
 */
export function checkIfInSchoolContext(host: string): boolean {
  const schoolSlug = extractSchoolSlug(host);
  const result = schoolSlug !== null;

  localLog("Checking school context:", {
    host,
    schoolSlug,
    isSchoolContext: result,
  });

  return result;
}
